import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Badge,
  HStack,
  VStack,
  Flex,
  Spinner,
  Alert,
  Textarea,
  Table,
  Dialog,
  NativeSelect,
  Tabs,
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';
import ViewAgentDetailsModal from '../components/ViewAgentDetailsModal';
import { exportPendingToExcel } from '../utils/exportUtils';

const API_URL = config.BACKEND_URL;

const PendingUploads = () => {
  const { user, canEditMain, canEditBranch } = useAuth();
  const [pendingUploads, setPendingUploads] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetails, setBatchDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedFileHash, setSelectedFileHash] = useState(null);
  const [hourFilter, setHourFilter] = useState('');
  const [availableHours, setAvailableHours] = useState([]);
  const [currentApprover, setCurrentApprover] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Tab state for Main/Branch
  const [activeTab, setActiveTab] = useState(() => {
    if (canEditMain()) return 'main';
    if (canEditBranch()) return 'branch';
    return 'main';
  });

  // View Modal State
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Tab change handler
  const handleTabChange = (details) => {
    const newTab = details.value;
    setActiveTab(newTab);
    setPendingUploads([]);
    setSelectedBatch(null);
    setBatchDetails([]);
    setError('');
  };

  useEffect(() => {
    if (!canEditMain() && canEditBranch()) {
      setActiveTab('branch');
    }
  }, [canEditMain, canEditBranch]);

  useEffect(() => {
    const controller = new AbortController();
    fetchPendingUploads(controller.signal);
    fetchCurrentApprover();
    return () => controller.abort();
  }, [activeTab]);

  const fetchCurrentApprover = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/approver-config`, { withCredentials: true });
      const activeApprover = response.data.find(approver => approver.is_active);
      setCurrentApprover(activeApprover);
    } catch (err) {
      console.error('Error fetching current approver:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      timeZone: 'Asia/Jakarta',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMembershipDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return 'Invalid Date';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const fetchPendingUploads = async (signal = null) => {
    try {
      setLoading(true);
      const axiosConfig = { withCredentials: true };
      if (signal) axiosConfig.signal = signal;

      // Use appropriate API endpoint based on activeTab
      const apiEndpoint = activeTab === 'branch'
        ? `${API_URL}/api/branch-approval/pending/grouped`
        : `${API_URL}/api/approval/pending/grouped`;

      const response = await axios.get(apiEndpoint, axiosConfig);
      setPendingUploads(response.data);

      const hours = [...new Set(response.data.map(upload => {
        const date = new Date(upload.upload_date);
        return date.toLocaleString('en-US', {
          timeZone: 'Asia/Jakarta',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          hour12: false
        });
      }))].sort().reverse();
      setAvailableHours(hours);
      setError('');
    } catch (err) {
      if (axios.isCancel(err)) return;
      setError('Failed to fetch pending uploads');
    } finally {
      if (!signal || !signal.aborted) setLoading(false);
    }
  };

  const fetchBatchDetails = async (fileHash) => {
    try {
      const apiEndpoint = activeTab === 'branch'
        ? `${API_URL}/api/branch-approval/pending/batch/${fileHash}`
        : `${API_URL}/api/approval/pending/batch/${fileHash}`;
      const response = await axios.get(apiEndpoint, { withCredentials: true });
      setBatchDetails(response.data);
      setSelectedBatch(fileHash);
    } catch (err) {
      setError('Failed to fetch batch details');
    }
  };

  const approveBatch = async (fileHash) => {
    try {
      const apiEndpoint = activeTab === 'branch'
        ? `${API_URL}/api/branch-approval/approve/batch/${fileHash}`
        : `${API_URL}/api/approval/approve/batch/${fileHash}`;
      await axios.post(apiEndpoint, {}, { withCredentials: true });
      alert('Batch approved successfully!');
      await fetchPendingUploads();
      setSelectedBatch(null);
      setBatchDetails([]);
    } catch (err) {
      setError('Failed to approve batch');
    }
  };

  const rejectBatch = async () => {
    try {
      const apiEndpoint = activeTab === 'branch'
        ? `${API_URL}/api/branch-approval/reject/batch/${selectedFileHash}`
        : `${API_URL}/api/approval/reject/batch/${selectedFileHash}`;
      await axios.post(apiEndpoint, { reason: rejectionReason }, { withCredentials: true });
      alert('Batch rejected successfully!');
      await fetchPendingUploads();
      setSelectedBatch(null);
      setBatchDetails([]);
      setShowRejectModal(false);
      setRejectionReason('');
      setSelectedFileHash(null);
    } catch (err) {
      setError('Failed to reject batch');
    }
  };

  const openRejectModal = (fileHash) => {
    setSelectedFileHash(fileHash);
    setShowRejectModal(true);
  };

  const handleViewDetails = (agent) => {
    setSelectedAgent(agent);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedAgent(null);
  };

  const handleExportPending = async () => {
    setExporting(true);
    try {
      const url = activeTab === 'branch'
        ? `${API_URL}/api/branch-approval/pending`
        : `${API_URL}/api/approval/pending`;
      const response = await axios.get(url, { withCredentials: true });
      const allPending = response.data;
      if (allPending.length === 0) {
        alert('No pending uploads to export.');
        return;
      }
      // Fetch City/Port data for export enrichment
      let allCityPortPics = [];
      try {
        const cpResponse = await axios.get(`${API_URL}/api/city-ports`, { withCredentials: true });
        allCityPortPics = cpResponse.data;
      } catch (err) {
        console.warn('Could not fetch city port PIC data:', err);
      }
      const tabLabel = activeTab === 'branch' ? 'Branch' : 'Main';
      await exportPendingToExcel(allPending, `Pending_Uploads_${tabLabel}_${new Date().toISOString().slice(0, 10)}`, allCityPortPics);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export pending uploads.');
    } finally {
      setExporting(false);
    }
  };

  const isCurrentUserApprover = () => {
    return currentApprover && user && user.email === currentApprover.approver_email;
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" pt="80px">
        <Container maxW="container.xl" py={8}>
          <Flex justify="center" align="center" py={16}>
            <Spinner size="xl" color="blue.500" mr={4} />
            <Text color="gray.600">Loading pending uploads...</Text>
          </Flex>
        </Container>
      </Box>
    );
  }

  const filteredUploads = hourFilter
    ? pendingUploads.filter(upload => {
      const uploadHour = new Date(upload.upload_date).toLocaleString('en-US', {
        timeZone: 'Asia/Jakarta',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hour12: false
      });
      return uploadHour === hourFilter;
    })
    : pendingUploads;

  return (
    <Box minH="100vh" bg="gray.50" pt="80px">
      <Container maxW="container.xl" py={8}>
        <Box textAlign="center" py={4} mb={6}>
          <Heading as="h1" size="2xl" color="purple.600" mb={3}>
            PENDING UPLOADS MANAGEMENT
          </Heading>
          <Text fontSize="xl" color="gray.500">
            Manage and approve bulk agent uploads
          </Text>
        </Box>

        {/* Main/Branch Tabs */}
        <Tabs.Root defaultValue="main" value={activeTab} onValueChange={handleTabChange} mb={6}>
          <Tabs.List>
            {canEditMain() && (
              <Tabs.Trigger value="main" px={6} py={2}>
                <Text fontWeight="medium">Main Uploads</Text>
              </Tabs.Trigger>
            )}
            {canEditBranch() && (
              <Tabs.Trigger value="branch" px={6} py={2}>
                <Text fontWeight="medium">Branch Uploads</Text>
              </Tabs.Trigger>
            )}
            <Tabs.Indicator />
          </Tabs.List>
        </Tabs.Root>

        {/* Current Tab Badge */}
        <Flex align="center" gap={3} mb={4}>
          <Badge colorPalette={activeTab === 'branch' ? 'orange' : 'purple'} fontSize="md" px={4} py={2} borderRadius="full">
            {activeTab === 'branch' ? 'Branch' : 'Main'} Pending Uploads
          </Badge>
          <Button
            colorPalette="purple"
            size="sm"
            onClick={handleExportPending}
            disabled={exporting || pendingUploads.length === 0}
          >
            {exporting ? 'Exporting...' : 'Export All to Excel'}
          </Button>
        </Flex>

        {error && (
          <Alert.Root status="error" mb={4} borderRadius="md">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Description>{error}</Alert.Description>
            </Alert.Content>
          </Alert.Root>
        )}

        {/* Filter Controls */}
        <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={6}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
            <HStack spacing={4}>
              <Text fontWeight="medium" color="gray.700">Filter by Upload Time:</Text>
              <NativeSelect.Root w="250px">
                <NativeSelect.Field
                  value={hourFilter}
                  onChange={(e) => setHourFilter(e.target.value)}
                >
                  <option value="">All Hours</option>
                  {availableHours.map(hour => (
                    <option key={hour} value={hour}>{hour}:00</option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </HStack>
            <Text color="gray.500" fontSize="sm">
              Showing {filteredUploads.length} of {pendingUploads.length} pending uploads
            </Text>
          </Flex>
        </Box>

        {/* Main Table */}
        <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
          {filteredUploads.length === 0 ? (
            <VStack py={16} spacing={2}>
              <Text fontSize="4xl">📭</Text>
              <Text color="gray.500">No pending uploads found.</Text>
            </VStack>
          ) : (
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row bg="gray.50">
                  <Table.ColumnHeader>File Name</Table.ColumnHeader>
                  <Table.ColumnHeader>Uploaded By</Table.ColumnHeader>
                  <Table.ColumnHeader>Upload Time</Table.ColumnHeader>
                  <Table.ColumnHeader>Records</Table.ColumnHeader>
                  <Table.ColumnHeader>Countries</Table.ColumnHeader>
                  <Table.ColumnHeader>Status</Table.ColumnHeader>
                  <Table.ColumnHeader>Actions</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredUploads.map((upload) => (
                  <Table.Row key={upload.file_hash} _hover={{ bg: 'gray.50' }}>
                    <Table.Cell>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="semibold" color="gray.800">{upload.original_filename}</Text>
                        <Text fontSize="xs" color="gray.500" fontFamily="mono">
                          Hash: {upload.file_hash.substring(0, 8)}...
                        </Text>
                      </VStack>
                    </Table.Cell>
                    <Table.Cell>{upload.uploader_display_name || upload.uploader}</Table.Cell>
                    <Table.Cell>{formatDate(upload.upload_date)}</Table.Cell>
                    <Table.Cell>
                      <VStack align="start" spacing={1}>
                        <Badge colorScheme="blue">{upload.record_count} total</Badge>
                        <HStack spacing={1}>
                          <Badge colorScheme="green" fontSize="xs">➕ {upload.insert_count} new</Badge>
                          <Badge colorScheme="purple" fontSize="xs">🔄 {upload.update_count} update</Badge>
                        </HStack>
                      </VStack>
                    </Table.Cell>
                    <Table.Cell maxW="200px">
                      <Text noOfLines={1}>
                        {upload.countries && upload.countries.length > 50
                          ? `${upload.countries.substring(0, 50)}...`
                          : upload.countries}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge colorScheme="yellow">Pending</Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <HStack spacing={2} flexWrap="wrap">
                        <Button size="xs" variant="outline" onClick={() => fetchBatchDetails(upload.file_hash)}>
                          View
                        </Button>
                        {isCurrentUserApprover() && (
                          <>
                            <Button size="xs" colorPalette="green" onClick={() => approveBatch(upload.file_hash)}>
                              Approve
                            </Button>
                            <Button size="xs" colorPalette="red" onClick={() => openRejectModal(upload.file_hash)}>
                              Reject
                            </Button>
                          </>
                        )}
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          )}
        </Box>

        {/* Batch Details Modal */}
        {selectedBatch && (
          <Dialog.Root open={true} onOpenChange={(e) => !e.open && (setSelectedBatch(null), setBatchDetails([]))}>
            <Dialog.Backdrop bg="blackAlpha.600" />
            <Dialog.Positioner>
              <Dialog.Content maxW="95vw" maxH="90vh" borderRadius="xl" overflow="hidden">
                <Dialog.Header bg="blue.50" py={4} px={6} borderBottom="1px" borderColor="gray.200">
                  <Text fontSize="lg" fontWeight="bold" color="blue.700">File Details</Text>
                </Dialog.Header>
                <Dialog.CloseTrigger />
                <Dialog.Body py={4} px={6} overflowY="auto">
                  <Box bg="gray.50" p={4} borderRadius="lg" mb={4}>
                    <HStack spacing={6} flexWrap="wrap">
                      <Text fontSize="sm"><strong>File:</strong> {batchDetails[0]?.original_filename}</Text>
                      <Text fontSize="sm"><strong>Records:</strong> {batchDetails.length}</Text>
                      <Text fontSize="sm"><strong>Uploaded by:</strong> {batchDetails[0]?.uploader_display_name || batchDetails[0]?.uploader}</Text>
                      <Text fontSize="sm"><strong>Time:</strong> {formatDate(batchDetails[0]?.upload_date)}</Text>
                    </HStack>
                  </Box>
                  <Box overflowX="auto" border="1px" borderColor="gray.200" borderRadius="lg">
                    <Table.Root size="sm">
                      <Table.Header>
                        <Table.Row bg="gray.50">
                          <Table.ColumnHeader>Country</Table.ColumnHeader>
                          <Table.ColumnHeader>Agent Name</Table.ColumnHeader>
                          <Table.ColumnHeader>Network</Table.ColumnHeader>
                          <Table.ColumnHeader>Class</Table.ColumnHeader>

                          <Table.ColumnHeader>FCL</Table.ColumnHeader>
                          <Table.ColumnHeader>LCL</Table.ColumnHeader>
                          <Table.ColumnHeader>Air</Table.ColumnHeader>
                          <Table.ColumnHeader>Inland</Table.ColumnHeader>
                          <Table.ColumnHeader>Consol Exp</Table.ColumnHeader>
                          <Table.ColumnHeader>Consol Imp</Table.ColumnHeader>
                          <Table.ColumnHeader>Oversea</Table.ColumnHeader>
                          <Table.ColumnHeader>Special Service</Table.ColumnHeader>
                          <Table.ColumnHeader>PIC Overseas</Table.ColumnHeader>
                          <Table.ColumnHeader>Status</Table.ColumnHeader>
                          <Table.ColumnHeader>Contract</Table.ColumnHeader>
                          <Table.ColumnHeader>Membership</Table.ColumnHeader>
                          <Table.ColumnHeader>Action</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {batchDetails.map((record) => (
                          <Table.Row key={record.id}>
                            <Table.Cell>{record.country}</Table.Cell>
                            <Table.Cell>{record.agent_name}</Table.Cell>
                            <Table.Cell>{record.network}</Table.Cell>
                            <Table.Cell>{record.class}</Table.Cell>

                            <Table.Cell><Badge colorPalette={record.fcl === 'YES' ? 'green' : 'red'} fontSize="xs">{record.fcl}</Badge></Table.Cell>
                            <Table.Cell><Badge colorPalette={record.lcl === 'YES' ? 'green' : 'red'} fontSize="xs">{record.lcl}</Badge></Table.Cell>
                            <Table.Cell><Badge colorPalette={record.air === 'YES' ? 'green' : 'red'} fontSize="xs">{record.air}</Badge></Table.Cell>
                            <Table.Cell><Badge colorPalette={record.inland === 'YES' ? 'green' : 'red'} fontSize="xs">{record.inland}</Badge></Table.Cell>
                            <Table.Cell><Badge colorPalette={record.consol_export ? 'teal' : 'gray'} fontSize="xs">{record.consol_export ? 'YES' : 'NO'}</Badge></Table.Cell>
                            <Table.Cell><Badge colorPalette={record.consol_import ? 'teal' : 'gray'} fontSize="xs">{record.consol_import ? 'YES' : 'NO'}</Badge></Table.Cell>
                            <Table.Cell><Badge colorPalette={record.consol_oversea ? 'teal' : 'gray'} fontSize="xs">{record.consol_oversea ? 'YES' : 'NO'}</Badge></Table.Cell>
                            <Table.Cell maxW="150px"><Text noOfLines={2} fontSize="xs">{record.special_service || 'N/A'}</Text></Table.Cell>
                            <Table.Cell>{record.pic_overseas}</Table.Cell>
                            <Table.Cell><Badge colorPalette={record.status === 'APPROVED' ? 'green' : record.status === 'POTENTIAL' ? 'yellow' : 'gray'} fontSize="xs">{record.status}</Badge></Table.Cell>
                            <Table.Cell><Badge colorPalette={record.contract === 'YES' ? 'green' : 'red'} fontSize="xs">{record.contract}</Badge></Table.Cell>
                            <Table.Cell>{record.membership_validity ? formatMembershipDate(record.membership_validity) : 'N/A'}</Table.Cell>
                            <Table.Cell>
                              <Button size="xs" variant="outline" colorPalette="blue" onClick={() => handleViewDetails(record)}>
                                View
                              </Button>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                </Dialog.Body>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Root>
        )}

        {/* Rejection Modal */}
        {showRejectModal && (
          <Dialog.Root open={true} onOpenChange={(e) => !e.open && (setShowRejectModal(false), setRejectionReason(''), setSelectedFileHash(null))}>
            <Dialog.Backdrop bg="blackAlpha.600" />
            <Dialog.Positioner>
              <Dialog.Content maxW="md" borderRadius="xl">
                <Dialog.Header bg="red.50" py={4} px={6} borderBottom="1px" borderColor="gray.200">
                  <Text fontSize="lg" fontWeight="bold" color="red.700">❌ Reject Upload Batch</Text>
                </Dialog.Header>
                <Dialog.CloseTrigger />
                <Dialog.Body py={6} px={6}>
                  <Text mb={4} color="gray.700">Please provide a reason for rejecting this upload batch:</Text>
                  <Textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter rejection reason..."
                    rows={4}
                  />
                </Dialog.Body>
                <Dialog.Footer bg="gray.50" px={6} py={4} borderTop="1px" borderColor="gray.200">
                  <HStack spacing={3} w="100%" justify="flex-end">
                    <Button variant="outline" onClick={() => {
                      setShowRejectModal(false);
                      setRejectionReason('');
                      setSelectedFileHash(null);
                    }}>
                      Cancel
                    </Button>
                    <Button
                      colorScheme="red"
                      onClick={rejectBatch}
                      disabled={!rejectionReason.trim()}
                    >
                      Reject Batch
                    </Button>
                  </HStack>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Root>
        )}
      </Container>

      {/* View Agent Details Modal */}
      {viewModalOpen && selectedAgent && (
        <ViewAgentDetailsModal
          agent={selectedAgent}
          onClose={handleCloseViewModal}
          formatDate={formatDate}
          dataType={activeTab}
        />
      )}
    </Box>
  );
};

export default PendingUploads;
